// =====================================================================
// EGL v12 Frontend - S3 ORPHAN IMAGE CLEANUP (ADMIN PANEL SECTION)
// Integrated cleanup management within admin panel
// =====================================================================

import React, { useState, useCallback, useEffect } from 'react';
import './OrphanImageCleanup.css';

// =====================================================================
// INTERFACES & TYPES
// =====================================================================

interface CleanupStatusData {
  current_orphans: number;
  statistics: {
    total_cleanups: number;
    total_images_cleaned: number;
    total_images_processed: number;
    last_cleanup_time: string | null;
  };
  recent_history: CleanupHistoryItem[];
}

interface CleanupHistoryItem {
  cleanup_type: 'MANUAL_CLEANUP' | 'AUTOMATED_CLEANUP';
  images_processed: number;
  images_cleaned: number;
  cleanup_details: {
    message: string;
    errors: string[];
    execution_time_ms?: number;
    timestamp: string;
  };
  executed_by: string;
  executed_at: string;
}

interface PendingOrphan {
  filename: string;
  certificate_id: string;
  age_hours: number;
  scheduled_deletion: string;
  s3_key: string;
}

interface CleanupResult {
  success: boolean;
  message: string;
  data?: {
    total_found: number;
    successfully_cleaned: number;
    failed: number;
    errors: string[];
    pending_orphans: PendingOrphan[];
    execution_time_ms: number;
    triggered_by: string;
    timestamp: string;
  };
  error?: string;
}

// =====================================================================
// MAIN COMPONENT
// =====================================================================

const OrphanImageCleanup: React.FC = () => {
  // =====================================================================
  // STATE MANAGEMENT
  // =====================================================================

  const [statusData, setStatusData] = useState<CleanupStatusData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCleaning, setIsCleaning] = useState<boolean>(false);
  const [lastCleanupResult, setLastCleanupResult] = useState<CleanupResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Settings state (removed - settings are now managed by backend scheduler)
  const [alert, setAlert] = useState<{type: 'success' | 'error' | 'warning'; message: string} | null>(null);

  // =====================================================================
  // API FUNCTIONS
  // =====================================================================

  /**
   * Fetch cleanup status and history
   */
  const fetchCleanupStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const token = localStorage.getItem('egl_token');
      const response = await fetch('/api/admin/s3/cleanup-status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        setStatusData(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch cleanup status');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      showAlert('error', `Failed to fetch status: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Trigger manual cleanup
   */
  const triggerCleanup = useCallback(async () => {
    try {
      setIsCleaning(true);
      setError(null);
      setLastCleanupResult(null);
      
      const token = localStorage.getItem('egl_token');
      const response = await fetch('/api/admin/s3/cleanup-orphans', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result: CleanupResult = await response.json();
      setLastCleanupResult(result);
      
      if (result.success) {
        if (result.data && result.data.successfully_cleaned > 0) {
          showAlert('success', `✅ Cleanup complete! ${result.data.successfully_cleaned} images cleaned in ${result.data.execution_time_ms}ms`);
        } else {
          showAlert('success', `✅ Cleanup complete! No orphaned images found.`);
        }
        
        // Refresh status after cleanup
        setTimeout(() => fetchCleanupStatus(), 1000);
      } else {
        showAlert('error', `❌ Cleanup failed: ${result.message}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      showAlert('error', `❌ Cleanup failed: ${errorMessage}`);
    } finally {
      setIsCleaning(false);
    }
  }, [fetchCleanupStatus]);

  // =====================================================================
  // ALERT MANAGEMENT
  // =====================================================================

  const showAlert = (type: 'success' | 'error' | 'warning', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  // =====================================================================
  // EFFECTS
  // =====================================================================

  // Initial load
  useEffect(() => {
    fetchCleanupStatus();
  }, [fetchCleanupStatus]);

  // =====================================================================
  // RENDER FUNCTIONS
  // =====================================================================

  /**
   * Render status widget
   */
  const renderStatusWidget = () => {
    if (!statusData) {
      return (
        <div className="s3-status-loading">
          <p>Loading status...</p>
        </div>
      );
    }

    const { current_orphans, statistics } = statusData;
    const orphanStatus = current_orphans === 0 ? 'healthy' : current_orphans < 10 ? 'warning' : 'critical';

    return (
      <div className="s3-status-grid">
        <div className={`s3-stat-card s3-stat-main s3-status-${orphanStatus}`}>
          <div className="s3-stat-value">{current_orphans}</div>
          <div className="s3-stat-label">Current Orphans</div>
          <div className="s3-stat-status">
            {orphanStatus === 'healthy' && '✅ All Clean'}
            {orphanStatus === 'warning' && '⚠️ Needs Attention'}
            {orphanStatus === 'critical' && '❌ Critical'}
          </div>
        </div>
        
        <div className="s3-stat-card">
          <div className="s3-stat-value">{statistics.total_cleanups}</div>
          <div className="s3-stat-label">Total Cleanups</div>
        </div>
        
        <div className="s3-stat-card">
          <div className="s3-stat-value">{statistics.total_images_cleaned}</div>
          <div className="s3-stat-label">Images Cleaned</div>
        </div>
        
        <div className="s3-stat-card">
          <div className="s3-stat-value">{statistics.total_images_processed}</div>
          <div className="s3-stat-label">Images Processed</div>
        </div>
      </div>
    );
  };

  /**
   * Render pending orphans (found but within grace period)
   */
  const renderPendingOrphans = () => {
    if (!lastCleanupResult || !lastCleanupResult.data || !lastCleanupResult.data.pending_orphans || lastCleanupResult.data.pending_orphans.length === 0) {
      return null;
    }

    const pendingOrphans = lastCleanupResult.data.pending_orphans;

    return (
      <div className="s3-pending-orphans">
        <div className="s3-pending-header">
          <h4>⏱️ Pending Orphans ({pendingOrphans.length})</h4>
          <p className="s3-pending-description">
            These orphaned images were found but are within the 24-hour grace period. They will be automatically deleted when scheduled.
          </p>
        </div>
        
        <div className="s3-pending-table">
          <table>
            <thead>
              <tr>
                <th>Filename</th>
                <th>Certificate ID</th>
                <th>Age</th>
                <th>Scheduled Deletion</th>
                <th>Time Remaining</th>
              </tr>
            </thead>
            <tbody>
              {pendingOrphans.map((orphan, idx) => {
                const scheduledTime = new Date(orphan.scheduled_deletion);
                const now = new Date();
                const hoursRemaining = Math.max(0, (scheduledTime.getTime() - now.getTime()) / (1000 * 60 * 60));
                const timeRemaining = hoursRemaining > 1 
                  ? `${hoursRemaining.toFixed(1)}h`
                  : `${(hoursRemaining * 60).toFixed(0)}m`;
                
                return (
                  <tr key={idx}>
                    <td className="s3-filename" title={orphan.s3_key}>
                      <span className="s3-file-icon">📄</span>
                      {orphan.filename}
                    </td>
                    <td className="s3-cert-id">
                      <span className="s3-cert-badge">{orphan.certificate_id}</span>
                    </td>
                    <td className="s3-age">
                      {orphan.age_hours.toFixed(1)}h
                    </td>
                    <td className="s3-scheduled-time">
                      {scheduledTime.toLocaleString()}
                    </td>
                    <td className="s3-time-remaining">
                      <span className="s3-countdown">
                        🕒 {timeRemaining}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        <div className="s3-pending-info">
          <span className="s3-info-icon">ℹ️</span>
          <p>
            These images were uploaded but no certificate was created. They will be automatically deleted during the next scheduled cleanup after the grace period expires.
          </p>
        </div>
      </div>
    );
  };

  /**
   * Render cleanup history table
   */
  const renderHistoryTable = () => {
    if (!statusData || statusData.recent_history.length === 0) {
      return <p className="s3-no-history">No cleanup history available.</p>;
    }

    return (
      <div className="s3-history-table">
        <table>
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Type</th>
              <th>Executed By</th>
              <th>Processed</th>
              <th>Cleaned</th>
              <th>Errors</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {statusData.recent_history.map((item, idx) => {
              const hasErrors = item.cleanup_details.errors && item.cleanup_details.errors.length > 0;
              
              return (
                <tr key={idx}>
                  <td className="s3-timestamp">
                    {new Date(item.executed_at).toLocaleString()}
                  </td>
                  <td>
                    <span className={`s3-type-badge s3-type-${item.cleanup_type.toLowerCase()}`}>
                      {item.cleanup_type === 'MANUAL_CLEANUP' ? '🖐️ Manual' : '🤖 Auto'}
                    </span>
                  </td>
                  <td>{item.executed_by}</td>
                  <td className="s3-number">{item.images_processed}</td>
                  <td className="s3-number s3-cleaned">{item.images_cleaned}</td>
                  <td className="s3-number">
                    {hasErrors ? (
                      <span className="s3-error-count" title={item.cleanup_details.errors.join('\n')}>
                        {item.cleanup_details.errors.length}
                      </span>
                    ) : (
                      <span className="s3-no-errors">0</span>
                    )}
                  </td>
                  <td>
                    <span className={`s3-status-icon ${hasErrors ? 's3-warning' : 's3-success'}`}>
                      {hasErrors ? '⚠️' : '✅'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // =====================================================================
  // MAIN RENDER
  // =====================================================================

  return (
    <div className="s3-cleanup-container">
      <div className="s3-cleanup-header">
        <h3>🗑️ S3 Orphan Image Cleanup</h3>
        <button 
          className="s3-refresh-btn"
          onClick={fetchCleanupStatus}
          disabled={isLoading}
          title="Refresh status"
        >
          🔄 Refresh
        </button>
      </div>

      {alert && (
        <div className={`s3-alert s3-alert-${alert.type}`}>
          <span>{alert.message}</span>
          <button onClick={() => setAlert(null)}>×</button>
        </div>
      )}

      {error && !alert && (
        <div className="s3-error-banner">
          <span>❌ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="s3-cleanup-content">
        {renderStatusWidget()}

        <div className="s3-cleanup-actions">
          <button
            className="s3-cleanup-btn"
            onClick={triggerCleanup}
            disabled={isCleaning || isLoading}
          >
            {isCleaning ? (
              <>
                <span className="s3-spinner"></span>
                Running Cleanup...
              </>
            ) : (
              '🗑️ Run Cleanup Now'
            )}
          </button>
          <p className="s3-cleanup-description">
            Manually trigger S3 orphan image cleanup. This will scan all S3 buckets and delete images older than 24 hours that are not linked to any certificate.
          </p>
        </div>

        {lastCleanupResult && (
          <div className={`s3-last-result s3-result-${lastCleanupResult.success ? 'success' : 'error'}`}>
            <div className="s3-result-header">
              <span className="s3-result-icon">
                {lastCleanupResult.success ? '✅' : '❌'}
              </span>
              <span className="s3-result-message">{lastCleanupResult.message}</span>
            </div>
            
            {lastCleanupResult.data && (
              <div className="s3-result-details">
                <div className="s3-result-stat">
                  <span>Found:</span> <strong>{lastCleanupResult.data.total_found}</strong>
                </div>
                <div className="s3-result-stat">
                  <span>Cleaned:</span> <strong>{lastCleanupResult.data.successfully_cleaned}</strong>
                </div>
                <div className="s3-result-stat">
                  <span>Failed:</span> <strong>{lastCleanupResult.data.failed}</strong>
                </div>
                <div className="s3-result-stat">
                  <span>Time:</span> <strong>{lastCleanupResult.data.execution_time_ms}ms</strong>
                </div>
                
                {lastCleanupResult.data.errors.length > 0 && (
                  <div className="s3-result-errors">
                    <strong>Errors:</strong>
                    <ul>
                      {lastCleanupResult.data.errors.map((error, idx) => (
                        <li key={idx}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {renderPendingOrphans()}

        <div className="s3-history-section">
          <h4>📜 Cleanup History (Last 20)</h4>
          {renderHistoryTable()}
        </div>
      </div>
    </div>
  );
};

export default OrphanImageCleanup;
